import { supabase } from '@/integrations/supabase/client';
import { parseVideoUrl, generateThumbnailUrl, generateEmbedUrl, SupportedPlatform } from './videoUtils';
import { CreateVideoRequest } from '@/types/video';

export interface AutoVideoCreationOptions {
  userId: string;
  videoUrl: string;
  // Optional metadata that can be provided from the source (sequence or display song)
  title?: string;
  description?: string;
  songName?: string;
  artistName?: string;
  albumName?: string;
  spotifyTrackId?: string;
  sequenceId?: string;
  displayId?: number;
  displayYear?: number;
  holiday?: string;
}

export interface AutoVideoCreationResult {
  success: boolean;
  videoId?: string;
  error?: string;
  wasExisting?: boolean; // true if video already existed
}

/**
 * Automatically create a video entry when a video URL is saved to sequences or display songs
 */
export async function createVideoFromUrl(options: AutoVideoCreationOptions): Promise<AutoVideoCreationResult> {
  try {
    const { userId, videoUrl } = options;

    // Parse and validate the video URL
    const parsed = parseVideoUrl(videoUrl);
    if (!parsed) {
      return {
        success: false,
        error: 'Invalid or unsupported video URL'
      };
    }

    const { platform, videoId } = parsed;

    // Check if a video with this URL or video ID + platform already exists
    const { data: existingVideo, error: checkError } = await supabase
      .from('videos')
      .select('id, sequence_id, display_id')
      .or(`video_url.eq.${videoUrl},and(video_id.eq.${videoId},video_platform.eq.${platform})`)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking for existing video:', checkError);
      return {
        success: false,
        error: 'Failed to check for existing video'
      };
    }

    // If video exists, optionally update relationships
    if (existingVideo) {
      const shouldUpdate = 
        (options.sequenceId && !existingVideo.sequence_id) ||
        (options.displayId && !existingVideo.display_id);

      if (shouldUpdate) {
        const updateData: any = {};
        if (options.sequenceId && !existingVideo.sequence_id) {
          updateData.sequence_id = options.sequenceId;
        }
        if (options.displayId && !existingVideo.display_id) {
          updateData.display_id = options.displayId;
        }

        const { error: updateError } = await supabase
          .from('videos')
          .update(updateData)
          .eq('id', existingVideo.id);

        if (updateError) {
          console.error('Error updating existing video relationships:', updateError);
        }
      }

      return {
        success: true,
        videoId: existingVideo.id,
        wasExisting: true
      };
    }

    // Generate metadata
    const thumbnailUrl = generateThumbnailUrl(platform, videoId);
    const embedUrl = generateEmbedUrl(platform, videoId);

    // Prepare video data
    const videoData: Omit<CreateVideoRequest, 'video_url'> & { 
      user_id: string; 
      video_url: string; 
      video_platform: SupportedPlatform; 
      video_id: string; 
      thumbnail_url?: string; 
    } = {
      user_id: userId,
      video_url: videoUrl,
      video_platform: platform,
      video_id: videoId,
      title: options.title || generateDefaultTitle(platform, options.songName, options.artistName),
      description: options.description,
      thumbnail_url: thumbnailUrl || undefined,
      song_name: options.songName,
      artist_name: options.artistName,
      album_name: options.albumName,
      spotify_track_id: options.spotifyTrackId,
      sequence_id: options.sequenceId,
      display_id: options.displayId,
      display_year: options.displayYear,
      holiday: options.holiday
    };

    // Create the video record
    const { data: newVideo, error: insertError } = await supabase
      .from('videos')
      .insert(videoData)
      .select('id')
      .single();

    if (insertError) {
      console.error('Error creating video:', insertError);
      
      // Handle duplicate key constraint errors gracefully
      if (insertError.code === '23505') {
        return {
          success: false,
          error: 'Video already exists'
        };
      }

      return {
        success: false,
        error: 'Failed to create video entry'
      };
    }

    return {
      success: true,
      videoId: newVideo.id,
      wasExisting: false
    };

  } catch (error) {
    console.error('Error in createVideoFromUrl:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Generate a default title for the video based on available metadata
 */
function generateDefaultTitle(platform: SupportedPlatform, songName?: string, artistName?: string): string {
  if (songName && artistName) {
    return `${songName} - ${artistName}`;
  } else if (songName) {
    return songName;
  } else if (artistName) {
    return `Video by ${artistName}`;
  } else {
    const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
    return `${platformName} Video`;
  }
}

/**
 * Helper function specifically for sequence video URLs
 */
export async function createVideoFromSequence(
  sequenceId: string,
  videoUrl: string,
  userId: string,
  sequenceData?: {
    title?: string;
    name?: string;
    artist?: string;
    album?: string;
    spotify_track_id?: string;
    holiday_type?: string;
  }
): Promise<AutoVideoCreationResult> {
  return createVideoFromUrl({
    userId,
    videoUrl,
    sequenceId,
    title: sequenceData?.title || sequenceData?.name,
    songName: sequenceData?.name,
    artistName: sequenceData?.artist,
    albumName: sequenceData?.album,
    spotifyTrackId: sequenceData?.spotify_track_id,
    holiday: sequenceData?.holiday_type
  });
}

/**
 * Helper function specifically for display song video URLs
 */
export async function createVideoFromDisplaySong(
  displayId: number,
  videoUrl: string,
  userId: string,
  songData?: {
    title?: string;
    artist?: string;
    genre?: string;
    year_introduced?: number;
    album_cover_url?: string;
  }
): Promise<AutoVideoCreationResult> {
  return createVideoFromUrl({
    userId,
    videoUrl,
    displayId,
    displayYear: songData?.year_introduced,
    title: songData?.title,
    songName: songData?.title,
    artistName: songData?.artist
  });
}